import { useState, useRef } from "react";
import { MOODS, MOOD_DESCRIPTIONS, type Mood } from "@shared/schema";

interface MoodRingProps {
  selected: Mood;
  onChange: (mood: Mood) => void;
  /** Overall diameter in px; geometry scales proportionally. Default 220. */
  size?: number;
}

const MOOD_ANGLES: Record<Mood, number> = {
  calm: 0,
  energized: 60,
  creative: 120,
  overwhelmed: 180,
  anxious: 240,
  drained: 300,
};

export function MoodRing({ selected, onChange, size = 220 }: MoodRingProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredMood, setHoveredMood] = useState<Mood | null>(null);

  const SIZE = size;
  const scale = size / 220;
  const CENTER = SIZE / 2;
  const OUTER_R = 90 * scale;
  const INNER_R = 58 * scale;
  const SEGMENT_R = 74 * scale;
  const ICON_R = 76 * scale;

  const getSegmentPath = (index: number) => {
    const total = MOODS.length;
    const angleStep = (2 * Math.PI) / total;
    const startAngle = index * angleStep - Math.PI / 2 - angleStep / 2;
    const endAngle = startAngle + angleStep;
    const gap = 0.04;

    const x1 = CENTER + OUTER_R * Math.cos(startAngle + gap);
    const y1 = CENTER + OUTER_R * Math.sin(startAngle + gap);
    const x2 = CENTER + OUTER_R * Math.cos(endAngle - gap);
    const y2 = CENTER + OUTER_R * Math.sin(endAngle - gap);
    const x3 = CENTER + INNER_R * Math.cos(endAngle - gap);
    const y3 = CENTER + INNER_R * Math.sin(endAngle - gap);
    const x4 = CENTER + INNER_R * Math.cos(startAngle + gap);
    const y4 = CENTER + INNER_R * Math.sin(startAngle + gap);

    return `M ${x1} ${y1} A ${OUTER_R} ${OUTER_R} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${INNER_R} ${INNER_R} 0 0 0 ${x4} ${y4} Z`;
  };

  const getLabelPosition = (index: number) => {
    const total = MOODS.length;
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    return {
      x: CENTER + ICON_R * Math.cos(angle),
      y: CENTER + ICON_R * Math.sin(angle),
    };
  };

  const handleClick = (mood: Mood) => {
    onChange(mood);
  };

  const displayMood = hoveredMood || selected;
  const accentColor = "var(--accent-color)";

  return (
    <div className="flex flex-col items-center gap-4 relative">
      <div data-testid="mood-ring">
        <svg
          ref={svgRef}
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="cursor-pointer"
        >
          {MOODS.map((mood, i) => {
            const isSelected = selected === mood;
            const isHovered = hoveredMood === mood;
            const pos = getLabelPosition(i);

            return (
              <g key={mood}>
                <path
                  d={getSegmentPath(i)}
                  fill={isSelected || isHovered ? "var(--accent-color)" : "transparent"}
                  stroke={isSelected ? "var(--accent-color)" : "var(--border-light)"}
                  strokeWidth={isSelected ? "1.5" : "1"}
                  opacity={isSelected ? 1 : isHovered ? 0.7 : 1}
                  onClick={() => handleClick(mood)}
                  onMouseEnter={() => setHoveredMood(mood)}
                  onMouseLeave={() => setHoveredMood(null)}
                  style={{ transition: "all 0.2s ease", cursor: "pointer" }}
                  data-testid={`mood-segment-${mood}`}
                />
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="9"
                  letterSpacing="0.18em"
                  fill={isSelected ? "var(--accent-color)" : "var(--text-secondary)"}
                  style={{
                    fontFamily: "var(--font-mono)",
                    transition: "fill 0.2s ease",
                    textTransform: "uppercase",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                >
                  {mood.toUpperCase()}
                </text>
              </g>
            );
          })}

          <circle
            cx={CENTER}
            cy={CENTER}
            r={INNER_R - 8}
            fill="transparent"
            stroke="var(--surface-3)"
            strokeWidth="1"
          />

          <text
            x={CENTER}
            y={CENTER - 10}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="13"
            fill={accentColor}
            style={{ fontFamily: "var(--font-display)", transition: "fill 0.4s ease", userSelect: "none" }}
          >
            {displayMood}
          </text>
          <text
            x={CENTER}
            y={CENTER + 12}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="9"
            letterSpacing="0.18em"
            fill="var(--text-muted)"
            style={{ fontFamily: "var(--font-mono)", userSelect: "none" }}
          >
            {MOOD_DESCRIPTIONS[displayMood].split(",")[0].toUpperCase()}
          </text>
        </svg>

        {selected && (
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
               background: "var(--accent-color)",
              opacity: 0.06,
              transition: "background-color 0.5s ease",
            }}
          />
        )}
      </div>
    </div>
  );
}
